import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  SimpleGrid,
  Group,
  Button,
  Stack,
  Box,
  Badge,
  ThemeIcon,
  Loader,
  Anchor,
  Avatar,
  Collapse,
  UnstyledButton,
  Divider,
} from '@mantine/core';
import {
  Plus,
  Calendar,
  CheckSquare,
  AlertTriangle,
  Clock,
  ChevronRight,
  ChevronDown,
  Activity,
} from 'react-feather';
import { useAuthStore } from '../../stores/auth';
import { tasksApi } from '../../services/api/tasks';
import { dashboardApi } from '../../services/api/dashboard';
import type {
  TaskWithDetails,
  DashboardActiveCohort,
  DashboardUpcomingSession,
  DashboardAttentionItem,
  DashboardAttentionSection,
  DashboardCohortCoachGroup,
  HealthSummaryMetrics,
} from '../../types';
import { formatDate, formatDateOnly } from '../../utils/formatters';
import { isDateOnlyPast, parseDateOnly } from '../../utils/dateHelpers';
import { HealthSummaryHeader } from '../cohorts/components/health/HealthSummaryHeader';
import classes from './DashboardPage.module.css';

// Health status display config
const HEALTH_CONFIG: Record<string, { label: string; color: string }> = {
  on_track: { label: 'Healthy', color: 'green' },
  monitor: { label: 'Monitor', color: 'orange' },
  at_risk: { label: 'At Risk', color: 'red' },
  no_data: { label: 'No Data', color: 'gray' },
};

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

// ============================================================================
// Active Cohorts Card (Left Box)
// ============================================================================

function ActiveCohortsCard({
  cohorts,
  loading,
}: {
  cohorts: DashboardActiveCohort[];
  loading: boolean;
}) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card withBorder padding="xl" className={classes.statusCard}>
        <Stack align="center" gap="md">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading cohorts...
          </Text>
        </Stack>
      </Card>
    );
  }

  if (cohorts.length === 0) {
    return (
      <Card withBorder padding="xl" className={classes.statusCard}>
        <Stack align="center" gap="md">
          <ThemeIcon size={56} radius="xl" variant="light" color="green">
            <Activity size={28} />
          </ThemeIcon>
          <Title order={4} ta="center">
            No active cohorts
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            Live cohorts will appear here once launched.
          </Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder padding="lg" className={classes.statusCard}>
      <Group gap="xs" mb="md">
        <ThemeIcon size={24} radius="xl" variant="light" color="green">
          <Activity size={14} />
        </ThemeIcon>
        <Title order={5}>Active Cohorts</Title>
      </Group>

      <Stack gap={4}>
        {cohorts.map((cohort) => {
          const health = HEALTH_CONFIG[cohort.health_status] || HEALTH_CONFIG.on_track;
          const isAtRisk = cohort.health_status === 'at_risk';

          return (
            <Box
              key={cohort.id}
              className={`${classes.clickableRow} ${isAtRisk ? classes.atRiskRow : ''}`}
              onClick={() => navigate(`/cohorts/${cohort.id}`)}
            >
              <Group justify="space-between" wrap="nowrap" mb={2}>
                <Text size="sm" fw={600} lineClamp={1} style={{ flex: 1 }}>
                  {cohort.name}
                </Text>
                <Group gap={6} wrap="nowrap">
                  <Badge size="xs" color="statusLive" variant="filled">
                    LIVE
                  </Badge>
                  <Badge size="xs" color={health.color} variant="light">
                    {health.label}
                  </Badge>
                </Group>
              </Group>
              <Text size="xs" c="dimmed">
                {cohort.partner_code || cohort.partner_name} &middot;{' '}
                {cohort.completed_sessions}/{cohort.total_sessions} sessions
              </Text>
            </Box>
          );
        })}
      </Stack>
    </Card>
  );
}

// ============================================================================
// Upcoming Sessions Card (Middle Box)
// ============================================================================

function UpcomingSessionsCard({
  sessions,
  loading,
}: {
  sessions: DashboardUpcomingSession[];
  loading: boolean;
}) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card withBorder padding="xl" className={classes.statusCard}>
        <Stack align="center" gap="md">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading sessions...
          </Text>
        </Stack>
      </Card>
    );
  }

  if (sessions.length === 0) {
    return (
      <Card withBorder padding="xl" className={classes.statusCard}>
        <Stack align="center" gap="md">
          <ThemeIcon size={56} radius="xl" variant="light" color="red">
            <Calendar size={28} />
          </ThemeIcon>
          <Title order={4} ta="center">
            No upcoming sessions
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            Sessions will appear here once scheduled.
          </Text>
        </Stack>
      </Card>
    );
  }

  const formatWeekOf = (dateStr: string) => {
    return `Week of ${formatDate(dateStr)}`;
  };

  return (
    <Card withBorder padding="lg" className={classes.statusCard}>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <ThemeIcon size={24} radius="xl" variant="light" color="red">
            <Calendar size={14} />
          </ThemeIcon>
          <Title order={5}>Upcoming Sessions</Title>
        </Group>
      </Group>

      <Box className={classes.scrollContainer}>
        <Stack gap={4}>
          {sessions.map((session, idx) => (
            <Box
              key={`${session.cohort_id}-${session.session_number}-${idx}`}
              className={classes.clickableRow}
              onClick={() => navigate(`/cohorts/${session.cohort_id}?tab=sessions`)}
            >
              <Group justify="space-between" wrap="nowrap" mb={2}>
                <Text size="sm" fw={600} lineClamp={1} style={{ flex: 1 }}>
                  Session {session.session_number}
                  {session.topic_name ? ` - ${session.topic_name}` : ''}
                </Text>
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {session.group_count} group{session.group_count !== 1 ? 's' : ''}
                </Text>
              </Group>
              <Group justify="space-between" wrap="nowrap">
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {session.cohort_name} &middot; {session.partner_name}
                </Text>
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {formatWeekOf(session.week_of)}
                </Text>
              </Group>
            </Box>
          ))}
        </Stack>
      </Box>
    </Card>
  );
}

// ============================================================================
// Cohorts Needing Attention Section
// ============================================================================

function AttentionSection({
  attention,
  loading,
}: {
  attention: DashboardAttentionSection | null;
  loading: boolean;
}) {
  const navigate = useNavigate();
  const [riskOpen, setRiskOpen] = useState(true);
  const [monitorOpen, setMonitorOpen] = useState(true);

  if (loading || !attention) return null;
  if (attention.risk.items.length === 0 && attention.monitor.items.length === 0) return null;

  // Group items by cohort for sub-grouping within each severity tier
  const groupByCohort = (items: DashboardAttentionItem[]) => {
    const grouped = new Map<string, { cohortId: string; cohortName: string; items: DashboardAttentionItem[] }>();
    for (const item of items) {
      const existing = grouped.get(item.cohort_id);
      if (existing) {
        existing.items.push(item);
      } else {
        grouped.set(item.cohort_id, { cohortId: item.cohort_id, cohortName: item.cohort_name, items: [item] });
      }
    }
    return Array.from(grouped.values());
  };

  const renderAlertRow = (item: DashboardAttentionItem) => (
    <Box key={item.alert.id} className={classes.attentionCard}>
      <Group wrap="nowrap" gap="md">
        <Box className={classes.metricBox}>
          <Text size="lg" fw={700} c="dark">
            {item.alert.statValue}
          </Text>
          <Text size="xs" c="dimmed" tt="uppercase" lts={0.3} style={{ fontSize: 10 }}>
            {item.alert.statLabel}
          </Text>
        </Box>
        <Box style={{ flex: 1 }}>
          <Text size="sm" fw={600}>
            {item.alert.title}
          </Text>
          <Text size="xs" c="dimmed">
            {item.alert.description}
          </Text>
        </Box>
        <Button
          size="xs"
          variant="light"
          color="medleyGreen"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/cohorts/${item.cohort_id}`);
          }}
        >
          View Cohort
        </Button>
      </Group>
    </Box>
  );

  const renderGroupedItems = (items: DashboardAttentionItem[]) => {
    const groups = groupByCohort(items);
    return (
      <Stack gap="sm" mt="xs">
        {groups.map(group => (
          <Box key={group.cohortId}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" lts={0.3} mb={4}>
              {group.cohortName}
            </Text>
            <Stack gap="xs">
              {group.items.map(renderAlertRow)}
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  };

  const totalItems = attention.risk.items.length + attention.monitor.items.length;
  const statusMessage = `${totalItems} item${totalItems !== 1 ? 's' : ''} needing attention`;

  const emptyMetrics: HealthSummaryMetrics = {
    engagementPercent: null,
    engagementAvg: null,
    engagementTrend: { direction: 'stable', change: 0, latestValue: null, averageValue: null },
    attendancePercent: null,
    attendanceTrend: { direction: 'stable', change: 0, latestValue: null, averageValue: null },
    avgRating: null,
    avgSatisfaction: null,
    satisfactionTopTwoBoxPct: null,
    ratingTrend: { direction: 'stable', change: 0, latestValue: null, averageValue: null },
  };

  return (
    <Card withBorder padding={0} styles={{ root: { boxShadow: 'none' } }}>
      <HealthSummaryHeader
        title="Cohort Health Nudges"
        statusMessage={statusMessage}
        metrics={emptyMetrics}
        recentSessions={[]}
        showMetrics={false}
      />
      <Box p="lg">
      {attention.risk.items.length > 0 && (
        <>
          <UnstyledButton
            className={classes.sectionHeader}
            onClick={() => setRiskOpen((o) => !o)}
            w="100%"
          >
            <Group justify="space-between">
              <Group gap="xs">
                <Text size="sm" c="red" fw={700}>
                  Risks
                </Text>
                <Text size="sm" c="dimmed">
                  &middot; {attention.risk.count} cohort
                  {attention.risk.count !== 1 ? 's' : ''}
                </Text>
              </Group>
              {riskOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </Group>
          </UnstyledButton>

          <Collapse in={riskOpen}>
            {renderGroupedItems(attention.risk.items)}
          </Collapse>
        </>
      )}

      {attention.risk.items.length > 0 && attention.monitor.items.length > 0 && (
        <Divider my="md" />
      )}

      {attention.monitor.items.length > 0 && (
        <>
          <UnstyledButton
            className={classes.sectionHeader}
            onClick={() => setMonitorOpen((o) => !o)}
            w="100%"
          >
            <Group justify="space-between">
              <Group gap="xs">
                <Text size="sm" c="orange" fw={700}>
                  Monitor
                </Text>
                <Text size="sm" c="dimmed">
                  &middot; {attention.monitor.count} cohort
                  {attention.monitor.count !== 1 ? 's' : ''}
                </Text>
              </Group>
              {monitorOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </Group>
          </UnstyledButton>

          <Collapse in={monitorOpen}>
            {renderGroupedItems(attention.monitor.items)}
          </Collapse>
        </>
      )}
      </Box>
    </Card>
  );
}

// ============================================================================
// Coaches Awaiting Contracts Section
// ============================================================================

function CoachesAwaitingContractsSection({
  cohortGroups,
  loading,
}: {
  cohortGroups: DashboardCohortCoachGroup[];
  loading: boolean;
}) {
  const navigate = useNavigate();

  if (loading) return null;
  if (cohortGroups.length === 0) return null;

  return (
    <Card withBorder padding="lg">
      <Group gap="xs" mb="md">
        <ThemeIcon size={24} radius="xl" variant="light" color="orange">
          <AlertTriangle size={14} />
        </ThemeIcon>
        <Title order={5}>Coaches Awaiting Contracts</Title>
      </Group>

      <Stack gap="md">
        {cohortGroups.map((group) => (
          <Box key={group.cohort_id}>
            <Text size="sm" fw={600} c="dimmed" mb="xs">
              {group.cohort_name}
            </Text>
            <Stack gap={4}>
              {group.coaches.map((coach) => (
                <Box
                  key={coach.user_id}
                  className={classes.clickableRow}
                  onClick={() => navigate(`/cohorts/${group.cohort_id}?tab=coaches`)}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap">
                      <Avatar
                        src={coach.avatar_url}
                        size={28}
                        radius="xl"
                        color="medleyGreen"
                      >
                        {coach.first_name[0]}
                        {coach.last_name[0]}
                      </Avatar>
                      <Text size="sm">
                        {coach.first_name} {coach.last_name}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                      {coach.announced_at
                        ? `Request sent: ${formatDate(coach.announced_at)}`
                        : 'Not yet announced'}
                    </Text>
                  </Group>
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Card>
  );
}

// ============================================================================
// Main Dashboard Page
// ============================================================================

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const firstName = user?.first_name || 'there';
  const greeting = getGreeting();

  // Tasks state
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [overdueCount, setOverdueCount] = useState(0);
  // MED-1394: the header text waits for both counts, so it does not change twice on load.
  const [loadingOverdue, setLoadingOverdue] = useState(true);

  // Dashboard state
  const [activeCohorts, setActiveCohorts] = useState<DashboardActiveCohort[]>([]);
  const [loadingCohorts, setLoadingCohorts] = useState(true);
  const [upcomingSessions, setUpcomingSessions] = useState<DashboardUpcomingSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [attention, setAttention] = useState<DashboardAttentionSection | null>(null);
  const [loadingAttention, setLoadingAttention] = useState(true);
  const [coachGroups, setCoachGroups] = useState<DashboardCohortCoachGroup[]>([]);
  const [loadingCoaches, setLoadingCoaches] = useState(true);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoadingTasks(true);
        const data = await tasksApi.getMyTasks(['not_started', 'in_progress', 'blocked'], 5);
        setTasks(data);
      } catch (error) {
        console.error('Failed to fetch tasks:', error);
      } finally {
        setLoadingTasks(false);
      }
    };

    const fetchActiveCohorts = async () => {
      try {
        setLoadingCohorts(true);
        const data = await dashboardApi.getActiveCohorts();
        setActiveCohorts(data);
      } catch (error) {
        console.error('Failed to fetch active cohorts:', error);
      } finally {
        setLoadingCohorts(false);
      }
    };

    const fetchUpcomingSessions = async () => {
      try {
        setLoadingSessions(true);
        const data = await dashboardApi.getUpcomingSessions();
        setUpcomingSessions(data);
      } catch (error) {
        console.error('Failed to fetch upcoming sessions:', error);
      } finally {
        setLoadingSessions(false);
      }
    };

    const fetchAttention = async () => {
      try {
        setLoadingAttention(true);
        const data = await dashboardApi.getAttentionItems();
        setAttention(data);
      } catch (error) {
        console.error('Failed to fetch attention items:', error);
      } finally {
        setLoadingAttention(false);
      }
    };

    const fetchCoaches = async () => {
      try {
        setLoadingCoaches(true);
        const data = await dashboardApi.getCoachesAwaitingContracts();
        setCoachGroups(data);
      } catch (error) {
        console.error('Failed to fetch coaches:', error);
      } finally {
        setLoadingCoaches(false);
      }
    };

    const fetchOverdueCount = async () => {
      if (!user?.id) {
        setLoadingOverdue(false);
        return;
      }
      try {
        const result = await tasksApi.getAllTasks({
          isOverdue: true,
          assigneeIds: [user.id],
          limit: 1,
          page: 1,
        });
        setOverdueCount(result.pagination.total);
      } catch (error) {
        console.error('Failed to fetch overdue count:', error);
      } finally {
        setLoadingOverdue(false);
      }
    };

    fetchTasks();
    fetchOverdueCount();
    fetchActiveCohorts();
    fetchUpcomingSessions();
    fetchAttention();
    fetchCoaches();
  }, [user?.id]);

  // Task stats
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueTodayTasks = tasks.filter((t) => {
    if (!t.due_date) return false;
    const dueDate = parseDateOnly(t.due_date);
    return dueDate?.getTime() === today.getTime();
  });

  return (
    <Container size="xl" py="xl">
      {/* Greeting Section */}
      <Box mb="xl">
        <Title order={1} className={classes.greeting}>
          {greeting}, {firstName}
        </Title>
        <Text size="lg" c="dimmed" mt="xs">
          {loadingTasks || loadingOverdue
            ? '\u00a0'
            : overdueCount > 0
            ? `You have ${overdueCount} overdue task${overdueCount === 1 ? '' : 's'} that need${overdueCount === 1 ? 's' : ''} attention.`
            : dueTodayTasks.length > 0
              ? `You have ${dueTodayTasks.length} task${dueTodayTasks.length === 1 ? '' : 's'} due today.`
              : tasks.length > 0
                ? `You have ${tasks.length} active task${tasks.length === 1 ? '' : 's'}. Keep up the good work!`
                : "You're all caught up!"}
        </Text>
      </Box>

      {/* Hero CTA Card */}
      <Card className={classes.heroCard} p="xl" mb="xl">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Box>
            <Badge size="sm" variant="light" color="medleyGold" mb="sm">
              New Partner Program
            </Badge>
            <Title order={2} c="white" mb="xs">
              Launch a new partner cohort
            </Title>
            <Text c="rgba(255,255,255,0.85)" maw={400}>
              Set up sessions, assign your team, and coordinate with your partner—all in one
              streamlined workflow.
            </Text>
          </Box>
          <Button
            size="md"
            variant="white"
            color="medleyGreen"
            leftSection={<Plus size={18} />}
            onClick={() => navigate('/cohorts/new')}
          >
            Create Cohort
          </Button>
        </Group>
      </Card>

      {/* Three-Card Row: Active Cohorts | Upcoming Sessions | My Tasks */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg" mb="xl">
        <ActiveCohortsCard cohorts={activeCohorts} loading={loadingCohorts} />

        <UpcomingSessionsCard sessions={upcomingSessions} loading={loadingSessions} />

        {/* My Tasks Card */}
        <Card withBorder padding="xl" className={classes.statusCard}>
          {loadingTasks ? (
            <Stack align="center" gap="md">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading tasks...
              </Text>
            </Stack>
          ) : tasks.length === 0 ? (
            <Stack align="center" gap="md">
              <ThemeIcon size={56} radius="xl" variant="light" color="green">
                <CheckSquare size={28} />
              </ThemeIcon>
              <Title order={4} ta="center">
                No tasks yet
              </Title>
              <Text size="sm" c="dimmed" ta="center">
                Your prep tasks and follow-ups will be tracked here as cohorts progress through
                their programs.
              </Text>
            </Stack>
          ) : (
            <Stack gap="sm">
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <ThemeIcon size={24} radius="xl" variant="light" color="green">
                    <CheckSquare size={14} />
                  </ThemeIcon>
                  <Title order={5}>My Tasks</Title>
                </Group>
                <Anchor size="sm" onClick={() => navigate('/tasks')}>
                  View all
                </Anchor>
              </Group>

              <Group gap="xs" mb="xs">
                {overdueCount > 0 && (
                  <Badge
                    color="red"
                    variant="light"
                    leftSection={<AlertTriangle size={12} />}
                  >
                    {overdueCount} overdue
                  </Badge>
                )}
                {dueTodayTasks.length > 0 && (
                  <Badge color="orange" variant="light" leftSection={<Clock size={12} />}>
                    {dueTodayTasks.length} due today
                  </Badge>
                )}
                <Badge color="gray" variant="light">
                  {tasks.length} active
                </Badge>
              </Group>

              <Stack gap={4}>
                {tasks.map((task) => (
                  <Box
                    key={task.id}
                    p="xs"
                    style={{
                      borderRadius: 4,
                      cursor: 'pointer',
                      backgroundColor: 'var(--mantine-color-gray-0)',
                    }}
                    onClick={() => navigate(`/cohorts/${task.cohort_id}?tab=tasks`)}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Text size="sm" lineClamp={1} style={{ flex: 1 }}>
                        {task.title}
                      </Text>
                      {task.due_date && (
                        <Text
                          size="xs"
                          c={isDateOnlyPast(task.due_date) ? 'red' : 'dimmed'}
                        >
                          {formatDateOnly(task.due_date)}
                        </Text>
                      )}
                    </Group>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
        </Card>
      </SimpleGrid>

      {/* Cohorts Needing Attention */}
      <Box mb="xl">
        <AttentionSection attention={attention} loading={loadingAttention} />
      </Box>

      {/* Coaches Awaiting Contracts */}
      <CoachesAwaitingContractsSection cohortGroups={coachGroups} loading={loadingCoaches} />
    </Container>
  );
}

export default DashboardPage;
